import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Cookie } from "lucide-react";
import { BrandLockup } from "@/components/LogoMark";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-display text-xl font-bold text-[#0A192F] mb-3">{title}</h2>
    <div className="text-[15px] text-slate-600 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <BrandLockup />
          <Link
            to="/"
            data-testid="cookie-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#0A192F] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </Link>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-3xl mx-auto px-5 sm:px-8 py-14"
      >
        <div className="flex items-center gap-3 mb-3">
          <Cookie className="w-6 h-6 text-[#10B981]" />
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Trasparenza sulla navigazione</p>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A192F] mb-2">
          Cookie Policy
        </h1>
        <p className="text-sm text-slate-500 mb-12">Ultimo aggiornamento: 4 novembre 2026</p>

        <Section title="1. Cosa sono i cookie">
          <p>
            I cookie sono piccoli file di testo che i siti salvano sul tuo dispositivo. Alcuni sono indispensabili
            per far funzionare il sito, altri servono a capire come viene usato. Su Casamatch.org usiamo anche il
            localStorage del browser, che funziona in modo analogo.
          </p>
        </Section>

        <Section title="2. Cookie tecnici (sempre attivi)">
          <p>Necessari al funzionamento del servizio, non richiedono consenso:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="text-[#0A192F]">Sessione area riservata</strong> — cookie di accesso del Titolare al pannello di gestione (durata: 12 ore);</li>
            <li><strong className="text-[#0A192F]">Contatti sbloccati</strong> — localStorage del browser che conserva i contatti che hai sbloccato dopo il pagamento, così li ritrovi quando torni (fino a cancellazione manuale);</li>
            <li><strong className="text-[#0A192F]">Preferenze tecniche</strong> — impostazioni di visualizzazione e sicurezza.</li>
          </ul>
        </Section>

        <Section title="3. Strumenti di analisi">
          <p>
            Utilizziamo <strong className="text-[#0A192F]">PostHog</strong> per statistiche aggregate sull'uso del sito
            (pagine visitate, percorsi di navigazione) allo scopo di migliorare il servizio. I dati sono trattati in
            forma aggregata e non vengono usati per profilazione pubblicitaria né ceduti a terzi per fini di marketing.
          </p>
        </Section>

        <Section title="4. Cosa NON usiamo">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Cookie di profilazione pubblicitaria o remarketing;</li>
            <li>Cookie di social network o pixel di tracciamento di terze parti a fini commerciali;</li>
            <li>Vendita dei dati di navigazione.</li>
          </ul>
        </Section>

        <Section title="5. Come gestire i cookie">
          <p>
            Puoi cancellare cookie e dati locali in qualsiasi momento dalle impostazioni del tuo browser
            (sezione "Privacy" o "Cronologia"). Attenzione: cancellando i dati locali perderai i contatti sbloccati
            salvati nel browser — li ritrovi comunque nell'email ricevuta dopo il pagamento.
          </p>
        </Section>

        <Section title="6. Titolare e contatti">
          <p>
            Titolare del trattamento: <strong className="text-[#0A192F]">Casamatch.org — La casa cerca te!</strong>,
            email <strong className="text-[#0A192F]">mediterraneumsrl@gmail.com</strong>. Per qualsiasi domanda su
            cookie e privacy puoi scriverci in qualsiasi momento. Vedi anche la{" "}
            <Link to="/privacy-policy" className="font-semibold text-[#0A192F] underline underline-offset-2">Privacy Policy</Link>.
          </p>
        </Section>
      </motion.main>
    </div>
  );
}
